import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { TaskProvider } from './contexts/TaskContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import Layout from './components/Layout';
import TaskDetailModal from './components/tasks/TaskDetailModal';
import { useTask } from './contexts/TaskContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import RequirePermission from './components/RequirePermission';
import ErrorBoundary from './components/ErrorBoundary';
import CustomerLogin from './pages/customer-portal/CustomerLogin';
import CustomerDashboard from './pages/customer-portal/CustomerDashboard';
import CustomerPortalOrders from './pages/customer-portal/CustomerOrders';
import CustomerPortalAccountMovements from './pages/customer-portal/CustomerAccountMovements';
import CustomerPortalAnalytics from './pages/customer-portal/CustomerAnalytics';
import CustomerTools from './pages/customer-portal/CustomerTools';
import CustomerDigitalPrinting from './pages/customer-portal/CustomerDigitalPrinting';
import CustomerProtectedRoute from './components/customer-portal/CustomerProtectedRoute';
import BrandingMetaSync from './components/BrandingMetaSync';
import PlatformLogin from './pages/platform/PlatformLogin';
import PlatformPanel from './pages/platform/PlatformPanel';
import PlatformProtectedRoute from './components/platform/PlatformProtectedRoute';

// Lazy loading - Sadece gerekli sayfalar başlangıçta yüklenir
const StokKartlari = lazy(() => import('./pages/StokKartlari'));
const CariKartlar = lazy(() => import('./pages/CariKartlar'));
const CariHesaplar = lazy(() => import('./pages/CariHesaplar'));
const HesapHareketleri = lazy(() => import('./pages/HesapHareketleri'));
const OdemeTahsilat = lazy(() => import('./pages/OdemeTahsilat'));
const DekontListesi = lazy(() => import('./pages/DekontListesi'));
const CekSenetDurumu = lazy(() => import('./pages/CekSenetDurumu'));
const FinansHareketleri = lazy(() => import('./pages/FinansHareketleri'));
const GiderYonetimi = lazy(() => import('./pages/GiderYonetimi'));
const Users = lazy(() => import('./pages/Users'));
const Definitions = lazy(() => import('./pages/Definitions'));
const DatabaseViewer = lazy(() => import('./pages/DatabaseViewer'));
const NotificationSettings = lazy(() => import('./pages/settings/NotificationSettings'));
const StokGiris = lazy(() => import('./pages/StokGiris'));
const StokCikis = lazy(() => import('./pages/StokCikis'));
const StokEnvanter = lazy(() => import('./pages/StokEnvanter'));
const StokHareketleri = lazy(() => import('./pages/StokHareketleri'));
const StokMaliyetleri = lazy(() => import('./pages/StokMaliyetleri'));
const StokDuzenlemeleri = lazy(() => import('./pages/StokDuzenlemeleri'));
const StokSifirlama = lazy(() => import('./pages/StokSifirlama'));
const StokDepoTransfer = lazy(() => import('./pages/StokDepoTransfer'));

// Üretim Takip Sayfaları (Lazy loaded)
const BoyahaneYonetimi = lazy(() => import('./pages/production/BoyahaneYonetimi'));
const OrmeYonetimi = lazy(() => import('./pages/production/OrmeYonetimi'));
const BaskiYonetimi = lazy(() => import('./pages/production/BaskiYonetimi'));

// Reports (Lazy loaded)
const SalesReports = lazy(() => import('./pages/reports/SalesReports'));
const ProfitAnalysis = lazy(() => import('./pages/reports/ProfitAnalysis'));
const AtilStoklar = lazy(() => import('./pages/reports/AtilStoklar'));
const PaymentCollectionReports = lazy(() => import('./pages/reports/PaymentCollectionReports'));
const StokPlanlama = lazy(() => import('./pages/reports/StokPlanlama'));
const CheckCreditActivityReport = lazy(() => import('./pages/reports/CheckCreditActivityReport'));

// Management Reports (Lazy loaded)
const BalanceSheet = lazy(() => import('./pages/management/BalanceSheet'));
const Personnel = lazy(() => import('./pages/management/Personnel'));

// Public Pages (Lazy loaded)
const PastelPublic = lazy(() => import('./pages/public/PastelPublic'));
const PublicCariView = lazy(() => import('./pages/public/PublicCariView'));

// Customer Management (Lazy loaded)
const CustomerOrders = lazy(() => import('./pages/customer-management/CustomerOrders'));
const CustomerPreOrders = lazy(() => import('./pages/customer-management/CustomerPreOrders'));
const CustomerFirstSales = lazy(() => import('./pages/customer-management/CustomerFirstSales'));

// NOTE: We avoid redirect-based permission key mismatch by serving StokGiris under both paths

// TaskDetailModal wrapper component (TaskContext kullanmak için)
const TaskDetailModalWrapper = () => {
  const { selectedTask, taskDetailOpen, closeTaskDetail } = useTask();
  
  if (!taskDetailOpen || !selectedTask) return null;
  
  return (
    <TaskDetailModal
      task={selectedTask}
      onClose={closeTaskDetail}
    />
  );
};

function App() {
  // Subdomain kontrolü
  const customerPortalHostnames = new Set([
    'musteri.jansoft.online',
    'customer.jansoft.online'
  ]);
  const isCustomerPortalSubdomain = customerPortalHostnames.has(window.location.hostname);
  
  // Müşteri portalı için CustomerAuthProvider ile
  if (isCustomerPortalSubdomain) {
    return (
      <Router>
        <ThemeProvider>
          <BrandingMetaSync />
          <CustomerAuthProvider>
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: {
                  maxWidth: '90vw',
                },
                success: {
                  duration: 3000,
                },
                error: {
                  duration: 4000,
                },
              }}
            />
            <Routes>
              <Route path="/login" element={<CustomerLogin />} />
              <Route path="/dashboard" element={
                <CustomerProtectedRoute>
                  <CustomerDashboard />
                </CustomerProtectedRoute>
              } />
              <Route path="/orders" element={
                <CustomerProtectedRoute>
                  <CustomerPortalOrders />
                </CustomerProtectedRoute>
              } />
              <Route path="/account-movements" element={
                <CustomerProtectedRoute>
                  <CustomerPortalAccountMovements />
                </CustomerProtectedRoute>
              } />
              <Route path="/analytics" element={
                <CustomerProtectedRoute>
                  <CustomerPortalAnalytics />
                </CustomerProtectedRoute>
              } />
              <Route path="/tools" element={
                <CustomerProtectedRoute>
                  <CustomerTools />
                </CustomerProtectedRoute>
              } />
              <Route path="/digital-printing" element={
                <CustomerProtectedRoute>
                  <CustomerDigitalPrinting />
                </CustomerProtectedRoute>
              } />
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </CustomerAuthProvider>
        </ThemeProvider>
      </Router>
    );
  }
  
  // Ana sistem için tüm provider'lar
  return (
    <Router>
      <ThemeProvider>
        <BrandingMetaSync />
        <AuthProvider>
          <NotificationProvider>
            <TaskProvider>
              <ErrorBoundary>
              <div className="App">
              <TaskDetailModalWrapper />
              <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'var(--toast-bg, #363636)',
                  color: 'var(--toast-color, #fff)',
                },
                loading: {
                  duration: 12000,
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                  style: {
                    background: 'var(--toast-success-bg, #10b981)',
                    color: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                  style: {
                    background: 'var(--toast-error-bg, #ef4444)',
                    color: '#fff',
                  },
                },
              }}
            />
            
            <Routes>
              {/* Ana sistem rotaları - müşteri portalı için buraya gelmez */}
              {!isCustomerPortalSubdomain && (
                <>
                  <Route path="/login" element={<Login />} />
                  <Route path="/musteri-portal/login" element={
                    <CustomerAuthProvider>
                      <CustomerLogin />
                    </CustomerAuthProvider>
                  } />
                  <Route path="/musteri-portal/dashboard" element={
                    <CustomerAuthProvider>
                      <CustomerProtectedRoute>
                        <CustomerDashboard />
                      </CustomerProtectedRoute>
                    </CustomerAuthProvider>
                  } />
                  <Route path="/musteri-portal/orders" element={
                    <CustomerAuthProvider>
                      <CustomerProtectedRoute>
                        <CustomerPortalOrders />
                      </CustomerProtectedRoute>
                    </CustomerAuthProvider>
                  } />
                  <Route path="/musteri-portal/account-movements" element={
                    <CustomerAuthProvider>
                      <CustomerProtectedRoute>
                        <CustomerPortalAccountMovements />
                      </CustomerProtectedRoute>
                    </CustomerAuthProvider>
                  } />
                  <Route path="/musteri-portal/analytics" element={
                    <CustomerAuthProvider>
                      <CustomerProtectedRoute>
                        <CustomerPortalAnalytics />
                      </CustomerProtectedRoute>
                    </CustomerAuthProvider>
                  } />
                  <Route path="/musteri-portal/tools" element={
                    <CustomerAuthProvider>
                      <CustomerProtectedRoute>
                        <CustomerTools />
                      </CustomerProtectedRoute>
                    </CustomerAuthProvider>
                  } />
                  <Route path="/musteri-portal/digital-printing" element={
                    <CustomerAuthProvider>
                      <CustomerProtectedRoute>
                        <CustomerDigitalPrinting />
                      </CustomerProtectedRoute>
                    </CustomerAuthProvider>
                  } />
                  <Route path="/musteri-portal" element={<Navigate to="/musteri-portal/login" replace />} />
                  <Route path="/platform/login" element={<PlatformLogin />} />
                  <Route path="/platform" element={
                    <PlatformProtectedRoute>
                      <PlatformPanel />
                    </PlatformProtectedRoute>
                  } />
                </>
              )}
              
              {/* Public Pages - No authentication required */}
              <Route path="/pastel" element={
                <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                  <PastelPublic />
                </Suspense>
              } />
              <Route path="/cari/:token" element={
                <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                  <PublicCariView />
                </Suspense>
              } />
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={
                  <RequirePermission>
                    <Dashboard />
                  </RequirePermission>
                } />
                <Route path="stock-cards" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><StokKartlari /></RequirePermission>
                  </Suspense>
                } />
                {/* Map both paths to the same page, but force the permission key to stock-entry.view */}
                <Route path="stok-giris" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission pathKey="stock-entry.view"><StokGiris /></RequirePermission>
                  </Suspense>
                } />
                <Route path="stock-entry" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission pathKey="stock-entry.view"><StokGiris /></RequirePermission>
                  </Suspense>
                } />
                <Route path="stock-exit" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><StokCikis /></RequirePermission>
                  </Suspense>
                } />
                <Route path="stock-inventory" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><StokEnvanter /></RequirePermission>
                  </Suspense>
                } />
                <Route path="stock-movements" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><StokHareketleri /></RequirePermission>
                  </Suspense>
                } />
                <Route path="stock-costs" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><StokMaliyetleri /></RequirePermission>
                  </Suspense>
                } />
                <Route path="stock-adjustments" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><StokDuzenlemeleri /></RequirePermission>
                  </Suspense>
                } />
                <Route path="stock-adjustments/reset" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission pathKey="stock-adjustments.view"><StokSifirlama /></RequirePermission>
                  </Suspense>
                } />
                <Route path="stock-adjustments/transfer" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission pathKey="stock-adjustments.view"><StokDepoTransfer /></RequirePermission>
                  </Suspense>
                } />
                <Route path="current-accounts" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><CariKartlar /></RequirePermission>
                  </Suspense>
                } />
                <Route path="finance/current-balances" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><CariHesaplar /></RequirePermission>
                  </Suspense>
                } />
                <Route path="finance/current-ledger" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><HesapHareketleri /></RequirePermission>
                  </Suspense>
                } />
                <Route path="finance/payments" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><OdemeTahsilat /></RequirePermission>
                  </Suspense>
                } />
                <Route path="finance/dekont" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><DekontListesi /></RequirePermission>
                  </Suspense>
                } />
                <Route path="finance/check-notes" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><CekSenetDurumu /></RequirePermission>
                  </Suspense>
                } />
                <Route path="finance/movements" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><FinansHareketleri /></RequirePermission>
                  </Suspense>
                } />
                <Route path="finance/expenses" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><GiderYonetimi /></RequirePermission>
                  </Suspense>
                } />
                
                {/* Üretim Takip Route'ları */}
                <Route path="uretim-takip/boyahane-yonetimi" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><BoyahaneYonetimi /></RequirePermission>
                  </Suspense>
                } />
                <Route path="uretim-takip/orme-yonetimi" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><OrmeYonetimi /></RequirePermission>
                  </Suspense>
                } />
                <Route path="uretim-takip/baski-yonetimi" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><BaskiYonetimi /></RequirePermission>
                  </Suspense>
                } />
                
                {/* Reports Route'ları */}
                <Route path="reports/sales" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><SalesReports /></RequirePermission>
                  </Suspense>
                } />
                <Route path="reports/profit" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><ProfitAnalysis /></RequirePermission>
                  </Suspense>
                } />
                <Route path="reports/idle-stocks" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><AtilStoklar /></RequirePermission>
                  </Suspense>
                } />
                <Route path="reports/payment-collection" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><PaymentCollectionReports /></RequirePermission>
                  </Suspense>
                } />
                <Route path="reports/stock-planning" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><StokPlanlama /></RequirePermission>
                  </Suspense>
                } />
                <Route path="reports/check-credit-activity" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><CheckCreditActivityReport /></RequirePermission>
                  </Suspense>
                } />
                
                {/* Management Reports Route'ları */}
                <Route path="management/balance-sheet" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission requireAdmin><BalanceSheet /></RequirePermission>
                  </Suspense>
                } />
                <Route path="management/personnel" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission requireAdmin><Personnel /></RequirePermission>
                  </Suspense>
                } />
                {/* Customer Management Routes */}
                <Route path="customer-management/orders" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><CustomerOrders /></RequirePermission>
                  </Suspense>
                } />
                <Route path="customer-management/pre-orders" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><CustomerPreOrders /></RequirePermission>
                  </Suspense>
                } />
                <Route path="customer-management/first-sales" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><CustomerFirstSales /></RequirePermission>
                  </Suspense>
                } />
                
                <Route path="users" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission requireAdmin><Users /></RequirePermission>
                  </Suspense>
                } />
                <Route path="definitions" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission><Definitions /></RequirePermission>
                  </Suspense>
                } />
                <Route path="settings/database" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission requireAdmin><DatabaseViewer /></RequirePermission>
                  </Suspense>
                } />
                <Route path="settings/notifications" element={
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Yükleniyor...</div>}>
                    <RequirePermission requireAdmin><NotificationSettings /></RequirePermission>
                  </Suspense>
                } />
                {/* Legacy routes removed from UI - redirect to dashboard */}
                <Route path="system-settings" element={<Navigate to="/" replace />} />
                <Route path="profile" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
              </div>
            </ErrorBoundary>
            </TaskProvider>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
